import React, { useEffect, useState, useMemo } from "react"
import PropTypes from "prop-types"
import MetaTags from "react-meta-tags"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import {
  Form,
  Input,
  Button,
  Card,
  Alert,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Tag,
  Spin,
  Result,
  Modal,
  message,
} from "antd"
import {
  SendOutlined,
  HomeOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  NumberOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FileTextOutlined,
  BarcodeOutlined,
} from "@ant-design/icons"

import {
  submitPackage,
  resetGasProjectFlag,
  getGasProjectFree,
} from "../../store/actions"
import logoSm from "../../assets/images/nazamlogo.png"
import "../Authentication/auth.scss"

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

const SubmitPackage = ({
  error,
  success,
  loading,
  gasProjectFree,
  submitPackage,
  resetGasProjectFlag,
  getGasProjectFree,
  location,
  history,
}) => {
  const [form] = Form.useForm()
  const [serialNumbers, setSerialNumbers] = useState([])

  // Calculate unit count
  const unitCount = useMemo(() => {
    return gasProjectFree?.unitNumber || 1
  }, [gasProjectFree])

  // Initialize serial numbers array when gasProjectFree changes
  useEffect(() => {
    if (gasProjectFree) {
      const existingSerials = gasProjectFree.packageSerialNumber
        ? gasProjectFree.packageSerialNumber
            .split(" | ")
            .map(item => item.split(": ")[1] || "")
        : []
      
      // const count = gasProjectFree.unitNumber || 1
      const count =  1
      setSerialNumbers(
        Array.from({ length: count }, (_, i) => existingSerials[i] || "")
      )
    }
  }, [gasProjectFree])

  useEffect(() => {
    if (error && typeof error === "string") {
      message.error({
        content: error,
        duration: 5,
        style: { marginTop: "20px" },
      })
    }
    if (success && typeof success === "string") {
      message.success({
        content: success,
        duration: 5,
        style: { marginTop: "20px" },
      })
    }
    if (error || success) {
      const timer = setTimeout(() => {
        resetGasProjectFlag()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, success, resetGasProjectFlag])

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const smsCode = query.get("c")
    const fileNumber = query.get("f")
    if (!smsCode || !fileNumber) {
      Modal.error({
        title: "خطا",
        content: "مشکل در شماره فایل و کد",
        okText: "متوجه شدم",
      })
    } else {
      getGasProjectFree(fileNumber)
    }
  }, [location.search, getGasProjectFree])

  // Convert Persian/Arabic numbers to English
  const convertToEnglishNumbers = (str) => {
    const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
    const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
    let result = str
    for (let i = 0; i < 10; i++) {
      result = result.replace(new RegExp(persianNumbers[i], 'g'), i.toString())
      result = result.replace(new RegExp(arabicNumbers[i], 'g'), i.toString())
    }
    return result
  }

  // Handle serial number change for multi-unit (only allow English alphanumeric)
  const handleSerialChange = (value, index) => {
    // Convert Persian/Arabic numbers to English, then remove non-alphanumeric
    const englishValue = convertToEnglishNumbers(value)
    const cleanValue = englishValue.replace(/[^a-zA-Z0-9]/g, "")
    const updated = [...serialNumbers]
    updated[index] = cleanValue
    setSerialNumbers(updated)
  }

  // Handle single unit serial change (only allow English alphanumeric)
  const handleSingleSerialChange = (e) => {
    // Convert Persian/Arabic numbers to English, then remove non-alphanumeric
    const englishValue = convertToEnglishNumbers(e.target.value)
    const cleanValue = englishValue.replace(/[^a-zA-Z0-9]/g, "")
    form.setFieldsValue({ serialNumber: cleanValue })
  }

  const handleSubmit = values => {
    const query = new URLSearchParams(location.search)
    const smsCode = query.get("c")
    const fileNumber = query.get("f")

    if (!smsCode || !fileNumber) {
      Modal.error({
        title: "خطا",
        content: "مشکل در شماره فایل و کد",
        okText: "متوجه شدم",
      })
      return
    }

    // Format serial number based on unit count
    let formattedSerial = values.serialNumber
    if (unitCount > 1) {
      // Check if any serial is empty
      const isEmpty = serialNumbers.some(s => !s?.trim())
      if (isEmpty) {
        Modal.error({
          title: "خطا",
          content: "لطفاً شماره سریال همه واحدها را وارد کنید",
          okText: "متوجه شدم",
        })
        return
      }
      formattedSerial = serialNumbers
        .map((sn, i) => `Vahed ${i + 1}: ${sn}`)
        .join(" | ")
    }

    Modal.confirm({
      title: "تأیید ارسال",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: "آیا از ارسال شماره سریال اطمینان دارید؟",
      okText: "بله، ارسال شود",
      cancelText: "انصراف",
      onOk: () => {
        submitPackage({ serialNumber: formattedSerial, smsCode, fileNumber }, history)
      },
    })
  }

  const getFloorText = () => {
    if (!gasProjectFree) return "-"
    // if (gasProjectFree.floorNumber === 0) {
    //   return gasProjectFree.unitNumber > 1 ? "چند واحدی" : "همکف"
    // }
    return `طبقه ${gasProjectFree.floorNumber == 0?'همکف':gasProjectFree.floorNumber}`
  }

  return (
    <div className="auth-container">
      <MetaTags>
        <title>ثبت شماره سریال پکیج | واحد گاز</title>
      </MetaTags>

      {/* Animated Background */}
      <div className="auth-background">
        <div className="auth-gradient-circle circle-1" />
        <div className="auth-gradient-circle circle-2" />
        <div className="auth-gradient-circle circle-3" />
      </div>

      <Row justify="center" align="middle" className="auth-row">
        <Col xs={23} sm={22} md={18} lg={14} xl={12} xxl={10}>
          <Card className="auth-card" bordered={false}>
            {/* Header */}
            <div className="auth-header">
              <img src={logoSm} alt="Logo" className="auth-logo main-logo" style={{ height: 70 }} />
              <Title level={4} className="auth-title">
                واحد نظارت گاز رسانی
              </Title>
              <Text type="secondary">
                سازمان نظام مهندسی استان کردستان
              </Text>
            </div>

            <Divider>
              <Space>
                <BarcodeOutlined />
                ثبت شماره سریال پکیج/آبگرمکن
              </Space>
            </Divider>

            {/* Project Information */}
            <Spin spinning={loading && !gasProjectFree}>
              {gasProjectFree ? (
                <div style={{ marginBottom: 24 }}>
                  {/* File Number Header */}
                  <div
                    style={{
                      background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                      padding: "16px",
                      borderRadius: "12px 12px 0 0",
                      textAlign: "center",
                    }}
                  >
                    <Space direction="vertical" size={4}>
                      <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>
                        شماره پرونده
                      </Text>
                      <Title level={4} style={{ color: "#fff", margin: 0 }}>
                        {gasProjectFree.fileNumber}
                      </Title>
                    </Space>
                  </div>

                  {/* Info Cards */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #f0f0f0",
                      borderTop: "none",
                      borderRadius: "0 0 12px 12px",
                      padding: "16px",
                    }}
                  >
                    {/* Owner & Unit Info */}
                    <Row gutter={[12, 12]}>
                      <Col xs={24} sm={12}>
                        <div
                          style={{
                            background: "#f6ffed",
                            borderRadius: 8,
                            padding: 12,
                            height: "100%",
                          }}
                        >
                          <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <UserOutlined style={{ marginLeft: 4 }} />
                              نام مالک
                            </Text>
                            <Text strong style={{ fontSize: 14 }}>
                              {gasProjectFree.landlordName || "-"}
                            </Text>
                          </Space>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          style={{
                            background: "#e6f7ff",
                            borderRadius: 8,
                            padding: 12,
                            height: "100%",
                          }}
                        >
                          <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <HomeOutlined style={{ marginLeft: 4 }} />
                              طبقه
                            </Text>
                            <Text strong style={{ fontSize: 14 }}>
                              {getFloorText()}
                            </Text>
                          </Space>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          style={{
                            background: "#fff7e6",
                            borderRadius: 8,
                            padding: 12,
                            height: "100%",
                          }}
                        >
                          <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <NumberOutlined style={{ marginLeft: 4 }} />
                              واحد
                            </Text>
                            <Text strong style={{ fontSize: 14 }}>
                              {gasProjectFree.unitDes || "-"}
                            </Text>
                          </Space>
                        </div>
                      </Col>
                    </Row>

                    {/* Address */}
                    <div
                      style={{
                        background: "#fafafa",
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 12,
                      }}
                    >
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          <EnvironmentOutlined style={{ marginLeft: 4 }} />
                          آدرس
                        </Text>
                        <Text style={{ fontSize: 13, lineHeight: 1.6 }}>
                          {gasProjectFree.address || "-"}
                        </Text>
                      </Space>
                    </div>

                    {/* Contact Info */}
                    <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                      <Col xs={12}>
                        <div
                          style={{
                            background: "#f9f0ff",
                            borderRadius: 8,
                            padding: 12,
                          }}
                        >
                          <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <NumberOutlined style={{ marginLeft: 4 }} />
                              کد پستی
                            </Text>
                            <Text strong style={{ fontSize: 13 }} copyable>
                              {gasProjectFree.postalCode || "-"}
                            </Text>
                          </Space>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div
                          style={{
                            background: "#fff0f6",
                            borderRadius: 8,
                            padding: 12,
                          }}
                        >
                          <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <PhoneOutlined style={{ marginLeft: 4 }} />
                              تلفن مالک
                            </Text>
                            <Text strong style={{ fontSize: 13 }} copyable>
                              {gasProjectFree.landlordPhoneNumber || "-"}
                            </Text>
                          </Space>
                        </div>
                      </Col>
                    </Row>

                    {/* Executor Info */}
                    <div
                      style={{
                        background: "linear-gradient(135deg, #e6fffb 0%, #f0f5ff 100%)",
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 12,
                      }}
                    >
                      <Row gutter={[12, 8]}>
                        <Col xs={24}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            <ToolOutlined style={{ marginLeft: 4 }} />
                            اطلاعات مجری
                          </Text>
                        </Col>
                        <Col xs={14}>
                          <Text strong style={{ fontSize: 13 }}>
                            {gasProjectFree.exeFullName || "-"}
                          </Text>
                        </Col>
                        <Col xs={10}>
                          <Text style={{ fontSize: 13 }} copyable>
                            {gasProjectFree.exeCellPhone || "-"}
                          </Text>
                        </Col>
                      </Row>
                    </div>

                    {/* Serial Number Status */}
                    <div
                      style={{
                        background: gasProjectFree.packageSerialNumber ? "#f6ffed" : "#fffbe6",
                        border: `1px solid ${gasProjectFree.packageSerialNumber ? "#b7eb8f" : "#ffe58f"}`,
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 12,
                        textAlign: "center",
                      }}
                    >
                      <Space direction="vertical" size={4}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          <BarcodeOutlined style={{ marginLeft: 4 }} />
                          شماره سریال فعلی
                        </Text>
                        {gasProjectFree.packageSerialNumber ? (
                          <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 14, padding: "4px 12px" }}>
                            {gasProjectFree.packageSerialNumber}
                          </Tag>
                        ) : (
                          <Tag color="warning" icon={<WarningOutlined />} style={{ fontSize: 13, padding: "4px 12px" }}>
                            هنوز ثبت نشده
                          </Tag>
                        )}
                      </Space>
                    </div>
                  </div>
                </div>
              ) : (
                !loading && (
                  <Result
                    status="warning"
                    title="مشخصات پرونده یافت نشد"
                    subTitle="اطلاعات پرونده ثبت نشده است یا لینک نامعتبر می‌باشد"
                    style={{ padding: "24px 0" }}
                  />
                )
              )}
            </Spin>

            {/* Form */}
            {gasProjectFree && (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                size="large"
              >
                {/* Already Submitted Alert */}
                {gasProjectFree.packageInstallerSubmit && (
                  <Alert
                    message="شماره سریال قبلاً ثبت شده است"
                    description={
                      <Space direction="vertical">
                        <Text>
                          شماره سریال این پرونده قبلاً توسط نصاب ثبت شده است و امکان ویرایش وجود ندارد.
                        </Text>
                        {gasProjectFree.packageSerialNumber && (
                          <Text>
                            شماره سریال ثبت شده:{" "}
                            <Text strong code>{gasProjectFree.packageSerialNumber}</Text>
                          </Text>
                        )}
                      </Space>
                    }
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    style={{ marginBottom: 16 }}
                  />
                )}

                {!gasProjectFree.packageInstallerSubmit && (
                  <div
                    style={{
                      background: "#fffbe6",
                      padding: 12,
                      borderRadius: 8,
                      marginBottom: 16,
                      border: "1px solid #ffe58f",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      <WarningOutlined style={{ color: "#faad14", marginLeft: 8 }} />
                      شماره سریال پکیج/آبگرمکن دیواری فن دار را به صورت حروف/عدد لاتین دقت وارد کنید.
                    </Text>
                  </div>
                )}

                {/* Single Unit - TextArea */}
                {unitCount <= 1 && (
                  <Form.Item
                    name="serialNumber"
                    label="شماره سریال"
                    rules={[
                      { required: !gasProjectFree.packageInstallerSubmit, message: "لطفاً شماره سریال را بصورت لاتین وارد کنید" },
                      { pattern: /^[a-zA-Z0-9]*$/, message: "فقط حروف و اعداد لاتین مجاز است" },
                    ]}
                  >
                    <TextArea
                      placeholder={gasProjectFree.packageInstallerSubmit ? "قبلاً ثبت شده است" : "شماره سریال دستگاه را وارد کنید..."}
                      rows={3}
                      style={{ borderRadius: 8 }}
                      disabled={gasProjectFree.packageInstallerSubmit}
                      onChange={handleSingleSerialChange}
                    />
                  </Form.Item>
                )}

                {/* Multiple Units - Separate Inputs */}
                {unitCount > 1 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      <BarcodeOutlined style={{ marginLeft: 8 }} />
                      {/* شماره سریال هر واحد ({unitCount} واحد) */}
                     شماره سریال را وارد کنید
                    </Text>
                    
                    {/* Info Alert - Same serial for shared package */}
                    {/* <Alert
                      message="توجه: در صورتی که واحدها از یک پکیج مشترک استفاده می‌کنند، شماره سریال یکسان را برای همه واحدها وارد کنید."
                      type="info"
                      showIcon
                      style={{ marginBottom: 12, fontSize: 12 }}
                    /> */}
                    
                    <Row gutter={[12, 12]}>
                      {serialNumbers.map((sn, index) => (
                        <Col xs={24} sm={12} key={`serial-${index}`}>
                          <div
                            style={{
                              background: "#fafafa",
                              borderRadius: 8,
                              padding: 12,
                              border: "1px solid #f0f0f0",
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                              <NumberOutlined style={{ marginLeft: 4 }} />
                              {/* {` واحد ${index + 1} : به صورت لاتین وارد کنید`} */}
                              به صورت لاتین وارد کنید
                            </Text>
                            <Input
                              value={sn}
                              // placeholder={`شماره سریال واحد ${index + 1}`}
                              onChange={e => handleSerialChange(e.target.value, index)}
                              disabled={gasProjectFree.packageInstallerSubmit}
                              style={{ borderRadius: 6 }}
                              onKeyDown={e => {
                                // Prevent special characters from being typed
                                if (!/[a-zA-Z0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                  e.preventDefault()
                                }
                              }}
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Installer Confirmation */}
                {gasProjectFree.packageInstaller && (
                  <Card
                    size="small"
                    style={{
                      marginBottom: 20,
                      background: "linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)",
                      borderColor: "#91d5ff",
                      borderRadius: 8,
                    }}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Space>
                        <SafetyCertificateOutlined style={{ color: "#1890ff", fontSize: 20 }} />
                        <Text strong style={{ color: "#1890ff" }}>
                          تعهدنامه نصاب
                        </Text>
                      </Space>
                      <Paragraph style={{ margin: 0, fontSize: 13, lineHeight: 2 }}>
                        اینجانب{" "}
                        <Text strong>{gasProjectFree.packageInstaller.fullName}</Text>{" "}
                        با کد ملی{" "}
                        <Text code>{gasProjectFree.packageInstaller.naCode}</Text>{" "}
                        نصاب مجاز شرکت{" "}
                        <Text strong>{gasProjectFree.packageInstaller.companyName}</Text>،{" "}
                        مسئولیت نصب دستگاه با شماره سریال وارد شده را در ملک با مشخصات فوق می‌پذیرم.
                      </Paragraph>
                    </Space>
                  </Card>
                )}

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={gasProjectFree.packageInstallerSubmit ? <CheckCircleOutlined /> : <SendOutlined />}
                    block
                    className="auth-submit-btn"
                    disabled={gasProjectFree.packageInstallerSubmit}
                  >
                    {gasProjectFree.packageInstallerSubmit 
                      ? "قبلاً ثبت شده است" 
                      : loading 
                        ? "در حال ارسال..." 
                        : "ثبت شماره سریال"}
                  </Button>
                </Form.Item>
              </Form>
            )}

            <Divider style={{ margin: "20px 0 12px" }} />

            {/* Copyright */}
            <Text type="secondary" className="copyright">
              © {new Date().getFullYear()} واحد گاز کردستان - تمامی حقوق محفوظ است
            </Text>
          </Card>
        </Col>
      </Row>
    </div>
  )
}


const mapStateToProps = state => {
  const { error, success, loading, gasProjectFree } = state.GasProjects
  return { error, loading, success, gasProjectFree }
}

export default withRouter(
  connect(mapStateToProps, {
    submitPackage,
    resetGasProjectFlag,
    getGasProjectFree,
  })(SubmitPackage)
)
